import { KPIItem, UpsellType, ColumnConfig } from "../types/index";

// Tab keys used in the Analytics Dashboard
export const tabKeys = ["summary", "bundles", "slide_cart", "post_purchase", "thank_you"];

const colors = [
    "#1F77B4", // Strong Blue
    "#FF7F0E", // Vivid Orange
    "#2CA02C", // Fresh Green
    "#D62728", // Clear Red
    "#9467BD", // Purple
    "#8C564B", // Brown
    "#E377C2", // Pink
    "#7F7F7F", // Medium Gray
    "#BCBD22", // Yellow-Green
    "#17BECF", // Cyan / Aqua
    "#AEC7E8", // Light Blue
];

const colorMap: Record<string, string> = {
    bundles: colors[0],
    slide_cart: colors[1],
    post_purchase: colors[2],
    thank_you: colors[3],

    standard: colors[0],
    bxgy: colors[1],
    volume: colors[2],
    mixmatch: colors[3],
    fbt: colors[4],
    product_add_on: colors[5],
    mixmatch_sectional: colors[6],
    product_add_on_popup: colors[7],
    bundle_builder: colors[8],
    bb_placeholder: colors[9],
    buy_n: colors[10],

    __upsell: colors[0],
    __additional_products: colors[1],

    upsell_1: colors[0],
    upsell_2: colors[1],

    thankYou: colors[0],
};

export const upsellTypeConfigs: Record<string, Omit<UpsellType, "checked" | "onChange">[]> = {
    summary: [
        {
            id: "bundles",
            label: "Bundles",
            color: colorMap.bundles,
        },
        {
            id: "slide_cart",
            label: "Slide cart",
            color: colorMap.slide_cart,
        },
        {
            id: "post_purchase",
            label: "Post Purchase",
            color: colorMap.post_purchase,
        },
        {
            id: "thank_you",
            label: "Thank You Page",
            color: colorMap.thank_you,
        },
    ],
    bundles: [
        { id: "standard", label: "Standard", color: colorMap.standard },
        { id: "bxgy", label: "BXGY", color: colorMap.bxgy },
        { id: "volume", label: "Volume", color: colorMap.volume },
        { id: "mixmatch", label: "Mix & Match", color: colorMap.mixmatch },
        { id: "fbt", label: "FBT", color: colorMap.fbt },
        { id: "product_add_on", label: "Product Add-on", color: colorMap.product_add_on },
        { id: "mixmatch_sectional", label: "Mix & Match Sectional", color: colorMap.mixmatch_sectional },
        { id: "product_add_on_popup", label: "Product Add-on Popup", color: colorMap.product_add_on_popup },
        { id: "bundle_builder", label: "Build Your Own Box", color: colorMap.bundle_builder },
        { id: "bb_placeholder", label: "Product Page Bundle Builder", color: colorMap.bb_placeholder },
        { id: "buy_n", label: "Buy N", color: colorMap.buy_n },
    ],
    slide_cart: [
        {
            id: "__upsell",
            label: "Upsells",
            color: colorMap.__upsell,
        },
        {
            id: "__additional_products",
            label: "Additional Products",
            color: colorMap.__additional_products,
        },
    ],
    post_purchase: [
        // {
        //     id: "upsell_1",
        //     label: "Post Purchase Upsell 1",
        //     color: colorMap.upsell_1,
        // },
        // {
        //     id: "upsell_2",
        //     label: "Post Purchase Upsell 2",
        //     color: colorMap.upsell_2,
        // },
        {
            id: "postPurchase",
            label: "Post Purchase",
            color: colorMap.post_purchase,
        },
    ],
    thank_you: [
        {
            id: "thankYou",
            label: "Thank You",
            color: colorMap.thankYou,
        },
    ],
};

// Define column configurations based on backend data structure
export const columnConfigs: Record<string, ColumnConfig> = {
    summary: {
        headings: ["Offer Title", "Category", "Revenue", "Total Orders", "AOV"],
        contentTypes: ["text", "text", "numeric", "numeric", "numeric"] as any[],
    },
    bundles: {
        headings: ["Offer Title", "Category", "Revenue", "Total Orders", "Views", "Clicks", "AOV"],
        contentTypes: ["text", "text", "numeric", "numeric", "numeric", "numeric", "numeric"] as any[],
    },
    slide_cart: {
        headings: ["Product Title", "Revenue", "Total Orders", "Total Views", "Conversion Rate"],
        contentTypes: ["text", "numeric", "numeric", "numeric", "numeric"] as any[],
    },
    post_purchase: {
        headings: ["Product Title", "Revenue", "Total Orders", "Total Views", "Conversion Rate"],
        contentTypes: ["text", "numeric", "numeric", "numeric", "numeric"] as any[],
    },
    thank_you: {
        headings: ["Product Title", "Revenue", "Total Orders", "Total Views", "Conversion Rate"],
        contentTypes: ["text", "numeric", "numeric", "numeric", "numeric"] as any[],
    },
};

// KPI configurations by tab - using real API data
export const getKPIConfig = (kpiData: any, currentTab: string): KPIItem[] => {
    // Use actual backend response data
    const configs = {
        summary: [
            {
                title: "Offer Revenue",
                value: kpiData?.total_revenue ? `$${parseFloat(kpiData.total_revenue).toFixed(2)}` : "$0.00",
            },
            {
                title: "Orders",
                value: kpiData?.total_orders || "0",
            },
            {
                title: "Average Upsell Value",
                value: kpiData?.aov ? `$${parseFloat(kpiData.aov).toFixed(2)}` : "$0.00",
            },
        ],
        bundles: [
            {
                title: "Offer Revenue",
                value: kpiData?.total_revenue ? `$${parseFloat(kpiData.total_revenue).toFixed(2)}` : "$0.00",
            },
            {
                title: "Orders",
                value: kpiData?.total_orders || "0",
            },
            {
                title: "Average Upsell Value",
                value: kpiData?.aov ? `$${parseFloat(kpiData.aov).toFixed(2)}` : "$0.00",
            },
            // {
            //     title:"Views",
            //     value: kpiData?.views || "0",
            // }
        ],
        slide_cart: [
            {
                title: "Upsell Revenue",
                value: kpiData?.total_revenue ? `$${parseFloat(kpiData.total_revenue).toFixed(2)}` : "$0.00",
            },
            {
                title: "Cart Views",
                value: kpiData?.total_views || "0",
            },
            {
                title: "Checkout Clicks",
                value: kpiData?.total_clicks || "0",
            },
        ],
        post_purchase: [
            {
                title: "Offer Revenue",
                value: kpiData?.total_revenue ? `$${parseFloat(kpiData.total_revenue).toFixed(2)}` : "$0.00",
            },
            {
                title: "Impressions",
                value: kpiData?.total_views || "0",
            },

            {
                title: "Average Upsell Value",
                value: kpiData?.aov ? `$${parseFloat(kpiData.aov).toFixed(2)}` : "$0.00",
            },
            {
                title: "Accepted Offers",
                value: kpiData?.total_orders || "0",
            },
        ],
        thank_you: [
            {
                title: "Offer Revenue",
                value: kpiData?.total_revenue ? `$${parseFloat(kpiData.total_revenue).toFixed(2)}` : "$0.00",
            },
            {
                title: "Impressions",
                value: kpiData?.total_views || "0",
            },
            {
                title: "Average Upsell Value",
                value: kpiData?.aov ? `$${parseFloat(kpiData.aov).toFixed(2)}` : "$0.00",
            },
            {
                title: "Accepted Offers",
                value: kpiData?.total_orders || "0",
            },
        ],
    };

    return configs[currentTab as keyof typeof configs] || configs.summary;
};

export const tabs = [
    {
        id: "summary",
        content: "Summary",
        accessibilityLabel: "Summary",
        panelID: "summary-panel",
        tabId: "summary",
        redirection_url: "/",
    },
    {
        id: "bundles",
        content: "Bundles",
        accessibilityLabel: "Bundles",
        panelID: "bundles-panel",
        tabId: "bundles",
        redirection_url: "/",
    },
    {
        id: "slide_cart",
        content: "Slide Cart",
        accessibilityLabel: "Slide Cart",
        panelID: "slide-cart-panel",
        tabId: "slide-cart",
        redirection_url: "/slide-cart",
    },
    {
        id: "post_purchase",
        content: "Post Purchase",
        accessibilityLabel: "Post Purchase",
        panelID: "post-purchase-panel",
        tabId: "post-purchase",
        redirection_url: "/post-purchase",
    },
    {
        id: "thank_you",
        content: "Thank You Page",
        accessibilityLabel: "Thank You Page",
        panelID: "thank-you-panel",
        tabId: "thank-you",
        redirection_url: "/thank-you-page",
    },
];

export const ranges = [
    {
        alias: "today",
        title: "Today",
        period: {
            since: new Date(new Date().setHours(0, 0, 0, 0)),
            until: new Date(new Date().setHours(23, 59, 59, 999)),
        },
    },
    {
        alias: "yesterday",
        title: "Yesterday",
        period: {
            since: new Date(new Date(new Date().setDate(new Date().getDate() - 1)).setHours(0, 0, 0, 0)),
            until: new Date(new Date(new Date().setDate(new Date().getDate() - 1)).setHours(23, 59, 59, 999)),
        },
    },
    {
        alias: "last_7_days",
        title: "Last 7 Days",
        period: {
            since: new Date(new Date(new Date().setDate(new Date().getDate() - 7)).setHours(0, 0, 0, 0)),
            until: new Date(new Date().setHours(23, 59, 59, 999)),
        },
    },
    {
        alias: "last_30_days",
        title: "Last 30 days",
        period: {
            since: new Date(new Date(new Date().setDate(new Date().getDate() - 30)).setHours(0, 0, 0, 0)),
            until: new Date(new Date().setHours(23, 59, 59, 999)),
        },
    },
    {
        alias: "last_90_days",
        title: "Last 90 days",
        period: {
            since: new Date(new Date(new Date().setDate(new Date().getDate() - 90)).setHours(0, 0, 0, 0)),
            until: new Date(new Date().setHours(23, 59, 59, 999)),
        },
    },
    {
        alias: "last_12_months",
        title: "Last 12 months",
        period: {
            since: new Date(new Date(new Date().setMonth(new Date().getMonth() - 12)).setHours(0, 0, 0, 0)),
            until: new Date(new Date().setHours(23, 59, 59, 999)),
        },
    },
    {
        alias: "all_time",
        title: "All Time",
        period: {
            since: new Date(0),
            until: new Date(new Date().setHours(23, 59, 59, 999)),
        },
    },
    {
        alias: "custom",
        title: "Custom",
        period: {
            since: new Date(new Date(new Date().setDate(new Date().getDate() - 7)).setHours(0, 0, 0, 0)),
            until: new Date(new Date().setHours(23, 59, 59, 999)),
        },
    },
];

// Reverse mapping from value to display name
export const BundleTypeDisplayNames: Record<string, string> = {
    bxgy: "BXGY",
    standard: "Standard",
    volume: "Volume Discount",
    mixmatch: "Mix & Match",
    mix_match: "Mix & Match",
    fbt: "FBT",
    product_add_on: "Product Addons",
    bundle_builder: "Build Your Own Box",
    bb_placeholder: "Product Page Bundle Builder",
    product_add_on_popup: "Product Addons Popup",
    mixmatch_sectional: "Mix & Match (Sectional)",
    buy_n: "Buy N",
    ai_bundles: "AI Bundles",
    bundle_builder_cta: "Bundle Builder CTA",
    bundles: "Offers",
    "bxgy-sectional": "BXGY Sectional",
    "slide-cart": "Slide Cart",
    "thank-you-page": "Thank You Page",
    __upsell: "Cart Upsell",
    __additional_products: "Additional Products",
    __freeGiftRewards: "Free Gift Rewards",
    upsell_1: "Upsell 1",
    upsell_2: "Upsell 2",
    thankYou: "Thank You Page",
    postPurchase: "Post Purchase",
};

export const categoryUrlMap: Record<string, string> = {
    bxgy: "/bxgy",
    standard: "/standard",
    volume: "/quantity-break",
    mixmatch: "/mixmatch",
    mix_match: "/mixmatch",
    fbt: "/fbt",
    product_add_on: "/product_add_on",
    bundle_builder: "/bundle-builder/byob",
    bb_placeholder: "/bundle-builder/placeholder",
    product_add_on_popup: "/product_add_on_popup",
    mixmatch_sectional: "/mixmatch-sectional",
    buy_n: "/buy-n",
    postPurchase: "/post-purchase",
    post_purchase: "/post-purchase",
    thankYou: "/thank-you-page",
    thank_you: "/thank-you-page",
    slide_cart: "/slide-cart",
    bundles: "/bundles",
};

export const categoryUrlMapOfferWise: Record<string, string> = {
    post_purchase: "/post-purchase",
    postPurchase: "/post-purchase",
    thank_you: "/thank-you-page",
    thankYou: "/thank-you-page",
    slide_cart: "/slide-cart",
};

export const offerWiseColumns = {
    headings: ["Offer Title", "Revenue", "Total Orders", "Views", "Items in Order"],
    contentTypes: ["text", "numeric", "numeric", "numeric", "numeric"],
};
